/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */

/**
 * POTableRow - Single row in the Purchase Orders table
 * Displays PO details, status badge, and action buttons (View, Issue, PDF, Archive)
 */
function POTableRow({ po, actionLoading, onIssue, onArchive, onPDF, onNavigate }) {
  const status = po.status?.toUpperCase() || 'DRAFT'
  const isLoading = actionLoading === po.id

  return (
    <tr
      data-testid={`po-table-row-${po.id}`}
      style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
      onClick={() => onNavigate(`/purchase-orders/${po.id}`)}
    >
      <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500', color: '#2563eb' }}>
        {po.po_number}
      </td>
      <td style={{ padding: '16px' }}>
        <div style={{ fontSize: '14px', color: '#374151' }}>{po.job_number || '-'}</div>
        {po.job_name && <div style={{ fontSize: '12px', color: '#6b7280' }}>{po.job_name}</div>}
      </td>
      <td style={{ padding: '16px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            width: '26px', height: '26px', background: '#1a5276', color: 'white',
            borderRadius: '50%', fontSize: '11px', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontWeight: '600'
          }}>{po.supplier_number}</span>
          <span style={{ fontSize: '14px', color: '#374151' }}>{po.supplier_name_snapshot}</span>
        </span>
      </td>
      <td style={{ padding: '16px', fontSize: '14px', fontWeight: '500', color: '#374151', textAlign: 'right' }}>
        ${parseFloat(po.total_amount || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
      </td>
      <td style={{ padding: '16px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
        <StatusBadge status={po.status} />
      </td>
      <td style={{ padding: '16px', fontSize: '14px', color: '#6b7280' }}>
        {new Date(po.created_at).toLocaleDateString()}
      </td>
      <td style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'nowrap' }}>
          {/* View - always available */}
          <button
            onClick={(e) => { e.stopPropagation(); onNavigate(`/purchase-orders/${po.id}`) }}
            style={{
              padding: '6px 12px', fontSize: '12px',
              backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
              borderRadius: '4px', cursor: 'pointer', fontWeight: '500', color: '#374151'
            }}
          >View</button>

          {/* Issue - only for DRAFT status */}
          {status === 'DRAFT' && (
            <button
              onClick={(e) => onIssue(e, po.id)}
              disabled={isLoading}
              style={{
                padding: '6px 12px', fontSize: '12px',
                backgroundColor: isLoading ? '#e5e7eb' : '#d1fae5',
                border: '1px solid #6ee7b7', borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: '500', color: isLoading ? '#9ca3af' : '#065f46'
              }}
            >{isLoading ? 'Issuing...' : 'Issue'}</button>
          )}

          {/* PDF - only for issued/archived */}
          {(status === 'ISSUED' || status === 'ARCHIVED') && (
            <button
              onClick={(e) => onPDF(e, po.id, po.po_number)}
              style={{
                padding: '6px 12px', fontSize: '12px',
                backgroundColor: '#dbeafe', border: '1px solid #93c5fd',
                borderRadius: '4px', cursor: 'pointer',
                fontWeight: '500', color: '#1e40af'
              }}
            >PDF</button>
          )}

          {/* Archive - for non-archived */}
          {status !== 'ARCHIVED' && (
            <button
              onClick={(e) => onArchive(e, po.id)}
              disabled={isLoading}
              style={{
                padding: '6px 12px', fontSize: '12px',
                backgroundColor: isLoading ? '#e5e7eb' : '#fee2e2',
                border: '1px solid #fecaca', borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: '500', color: isLoading ? '#9ca3af' : '#991b1b'
              }}
            >Archive</button>
          )}
        </div>
      </td>
    </tr>
  )
}

/** Status badge with color coding - matches QCS styling */
function StatusBadge({ status }) {
  const s = status?.toUpperCase() || 'DRAFT'
  const base = { padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', cursor: 'default' }
  const styles = {
    DRAFT: { ...base, backgroundColor: '#fef3c7', color: '#92400e' },
    ISSUED: { ...base, backgroundColor: '#d1fae5', color: '#065f46' },
    ARCHIVED: { ...base, backgroundColor: '#e5e7eb', color: '#6b7280' }
  }
  const displayText = s === 'ISSUED' ? 'Issued' : (s === 'ARCHIVED' ? 'Archived' : 'Draft')
  return <span style={styles[s] || styles.DRAFT}>{displayText}</span>
}

export default POTableRow
